import * as React from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
    React.ElementRef<typeof AccordionPrimitive.Item>,
    React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
    <AccordionPrimitive.Item ref={ref} className={cn("setu:!border-b setu:!border-zinc-100", className)} {...props} />
));
AccordionItem.displayName = "AccordionItem";

const AccordionTrigger = React.forwardRef<
    React.ElementRef<typeof AccordionPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
    <AccordionPrimitive.Header className="setu:flex">
        <AccordionPrimitive.Trigger
            ref={ref}
            className={cn(
                "setu:flex setu:flex-1 setu:items-center setu:justify-between setu:py-4  setu:!text-sm setu:!font-medium setu:!text-left setu:transition-all setu:hover:underline [&[data-state=open]>svg]:rotate-180",
                className
            )}
            {...props}
        >
            {children}
            <ChevronDown className="setu:text-muted-foreground setu:pointer-events-none setu:size-4 setu:shrink-0 setu:translate-y-0.5 setu:transition-transform setu:duration-200" />
        </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
    React.ElementRef<typeof AccordionPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
    <AccordionPrimitive.Content
        ref={ref}
        className="setu:overflow-hidden setu:text-sm setu:transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
        {...props}
    >
        <div className={cn("setu:pb-4 setu:pt-0", className)}>{children}</div>
    </AccordionPrimitive.Content>
));

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

// Fictoan compatibility interfaces and components
export interface FictoanExpandableContentProps {
    summary: React.ReactNode;
    open?: boolean;
    // Fictoan common props
    margin?: "none" | "nano" | "micro" | "tiny" | "small" | "medium" | "large" | "huge";
    padding?: "none" | "nano" | "micro" | "tiny" | "small" | "medium" | "large" | "huge";
    bgColour?: string;
    textColour?: string;
    borderColour?: string;
    shadow?: "none" | "mild" | "soft" | "hard";
    isFullWidth?: boolean;
    className?: string;
    children: React.ReactNode;
}

// Fictoan-compatible ExpandableContent wrapper
export const ExpandableContent = React.forwardRef<HTMLDivElement, FictoanExpandableContentProps>(
    ({ summary, open, children, className }, ref) => {
        const [isOpen, setIsOpen] = React.useState(open || false);

        return (
            <div ref={ref} className={cn("setu:w-full", className)}>
                <Accordion
                    type="single"
                    collapsible
                    value={isOpen ? "item-1" : undefined}
                    onValueChange={(value) => setIsOpen(value === "item-1")}
                >
                    <AccordionItem value="item-1">
                        <AccordionTrigger>{summary}</AccordionTrigger>
                        <AccordionContent>{children}</AccordionContent>
                    </AccordionItem>
                </Accordion>
            </div>
        );
    }
);

ExpandableContent.displayName = "ExpandableContent";

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
